import React from 'react';
import { useLanguage } from '../context/LanguageContext';
import { translations } from '../lib/translations';
import Section from '../components/UI/Section';
import { Phone, Mail, MapPin, Clock, CreditCard, Info, Map } from 'lucide-react';
import { motion } from 'framer-motion';

const Contact = () => {
  const { lang } = useLanguage();
  const t = translations[lang];

  const content = {
    da: {
      title: "Kontakt og info",
      subtitle: "",
      address: {
        name: "Psykolog Kay Elizabeth Worsfold",
        company: "Psykologerne ved Frue Plads",
        street: "Store Kannikestræde 18, 2. th.",
        city: "1169 København K"
      },
      prices: {
        title: "Priser (selvbetalende klienter)",
        date: "pr. 1.04.26",
        items: [
          "Individuel samtale: 1.375 kr. for 50 minutter.",
          "Individuel supervision: 1.400 kr. for 60 minutter."
        ],
        note: "Øvrige priser efter aftale."
      },
      insurance: {
        title: "Sundhedsforsikringer",
        text: "Hvis du eller din arbejdsplads har en sundhedsforsikring, kan du undersøge om den dækker psykologsamtaler."
      },
      sygeforsikring: {
        title: "Sygeforsikringen 'danmark'",
        text: "Hvis du er medlem af Sygeforsikringen ‘danmark’, og ønsker at jeg indberetter samtaler (forudsat at du kommer med en problematik, der hører ind under deres henvisningskriterier), oplys det venligst ved vores første samtale."
      },
      referral: {
        title: "Henvisning fra egen læge",
        text: "Jeg har overenskomst med sygesikringen. Har du en henvisning fra egen læge, bedes du venligst henvende dig pr. mail på: info@psykolog-kayworsfold.dk.",
        prices: [],
        note: ""
      },
      cancellation: {
        title: "Afbud",
        text: "Jeg beder om, at du melder et eventuelt afbud i så god tid, du kan. Melder du afbud senere end kl. 12 dagen før din aftale – eller udebliver du – betales et beløb svarende til det fulde honorar for samtalen. Dette gælder også, hvis du har tid om mandagen eller dagen efter en helligdag."
      },
      transport: {
        title: "Find vej",
        text: "Min klinik ligger i Store Kannikestræde i hjertet af København – nær Vor Frue Kirke og Rundetaarn. Klinikken ligger 5 minutters gang fra Nørreport station, hvor der også er metro og mange busforbindelser. Der er få minutters gang til metrostationen, Gammel Strand.",
        parking: "Kommer du i bil til klinikken, er der parkeringsmuligheder i Q-Park, Nørreport, ved Israels Plads. Der er også mulighed for at parkere på Nørre Voldgade og Gothersgade."
      },
      memberships: [
        {
          name: "Medlem af Dansk Psykolog Forening",
          logo: "https://assets.auxodev.dk/assets/d2a473f1-f23b-48f1-982c-7e943d864f39/image_2.png",
          link: "https://www.dp.dk/"
        },
        {
          name: "Medlem af Mindfulness Foreningen",
          logo: "https://assets.auxodev.dk/assets/d2a473f1-f23b-48f1-982c-7e943d864f39/deabdddf-d4e2-4cf0-998a-61ead6ea5996.png",
          link: "https://mindfulnessforeningen.dk/"
        },
        {
          name: "Registreret med det internationale ACCESS MBCT-register",
          logo: "https://assets.auxodev.dk/assets/d2a473f1-f23b-48f1-982c-7e943d864f39/image_3.png",
          link: "https://www.accessmbct.com/"
        }
      ]
    },
    en: {
      title: "Contact and Info",
      subtitle: "",
      address: {
        name: "Psychologist Kay Elizabeth Worsfold",
        company: "Psykologerne ved Frue Plads",
        street: "Store Kannikestræde 18, 2. th.",
        city: "1169 Copenhagen K"
      },
      prices: {
        title: "Rates",
        date: "As of 1.04.26",
        items: [
          "Individual adult psychotherapy (per 50-minute session) DKK 1375,- (Initial session is 60 minutes).",
          "Individual Supervision (per 60-minute session) DKK 1400,-"
        ],
        note: "Other prices by agreement."
      },
      insurance: {
        title: "Health Insurance",
        text: "If you or your workplace have health insurance you can check if it covers psychologist consultations."
      },
      sygeforsikring: {
        title: "Sygeforsikringen 'danmark'",
        text: "If you have coverage with Sygeforsikringen 'danmark', please let me know at our first consultation"
      },
      referral: {
        title: "GP Referral",
        text: "I have an agreement with the public health insurance. If you have a referral from your GP, please contact me by email at: info@psykolog-kayworsfold.dk.",
        prices: [],
        note: ""
      },
      cancellation: {
        title: "Cancellation",
        text: "I ask that you cancel any appointment as early as possible. If you cancel later than 12:00 the day before your appointment – or if you fail to show up – a fee corresponding to the full session fee is paid. This also applies if you have an appointment on Monday or the day after a public holiday."
      },
      transport: {
        title: "Getting here",
        text: "My clinic is located in Store Kannikestræde in the heart of Copenhagen – near Vor Frue Kirke and Rundetaarn. The clinic is a 5-minute walk from Nørreport station, where there is also metro and many bus connections. It is a few minutes’ walk from Gammel Strand metro station.",
        parking: "If you come by car, there are parking options in Q-Park, Nørreport, at Israels Plads. There is also the possibility of parking on Nørre Voldgade and Gothersgade."
      },
      memberships: [
        {
          name: "Member of the Danish Psychological Association",
          logo: "https://assets.auxodev.dk/assets/d2a473f1-f23b-48f1-982c-7e943d864f39/image_2.png",
          link: "https://www.dp.dk/"
        },
        {
          name: "Member of the Mindfulness Association",
          logo: "https://assets.auxodev.dk/assets/d2a473f1-f23b-48f1-982c-7e943d864f39/deabdddf-d4e2-4cf0-998a-61ead6ea5996.png",
          link: "https://mindfulnessforeningen.dk/"
        },
        {
          name: "Registered with the international ACCESS MBCT register",
          logo: "https://assets.auxodev.dk/assets/d2a473f1-f23b-48f1-982c-7e943d864f39/image_3.png",
          link: "https://www.accessmbct.com/"
        }
      ]
    }
  };

  const c = content[lang];

  return (
    <div className="pt-20">
      {/* Hero Section */}
      <section className="bg-stone-50 py-12 md:py-16 lg:py-32 border-b border-stone-100">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <h1 className="text-4xl md:text-5xl font-serif text-stone-900 mb-6">{c.title}</h1>
              {c.subtitle && <p className="text-xl text-stone-600 font-light mb-12">{c.subtitle}</p>}
              
              <div className="space-y-8">
                <div className="flex items-start gap-6">
                  <div className="w-12 h-12 bg-white rounded-full shadow-sm flex items-center justify-center shrink-0">
                    <MapPin className="w-5 h-5 text-[#c16e5e]" />
                  </div>
                  <div>
                    <p className="text-stone-900 font-medium mb-1">{c.address.name}</p>
                    <p className="text-stone-500 text-sm">{c.address.company}</p>
                    <p className="text-stone-500 text-sm">{c.address.street}</p>
                    <p className="text-stone-500 text-sm">{c.address.city}</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-6">
                  <div className="w-12 h-12 bg-white rounded-full shadow-sm flex items-center justify-center shrink-0">
                    <Mail className="w-5 h-5 text-[#c16e5e]" />
                  </div>
                  <div>
                    <p className="text-stone-400 text-[10px] uppercase tracking-widest mb-1">{lang === 'da' ? 'MAIL' : 'EMAIL'}</p>
                    <a href="mailto:info@psykolog-kayworsfold.dk" className="text-lg text-stone-900 hover:text-[#c16e5e] transition-colors">
                      info@psykolog-kayworsfold.dk
                    </a>
                  </div>
                </div>
                
                <div className="flex items-start gap-6">
                  <div className="w-12 h-12 bg-white rounded-full shadow-sm flex items-center justify-center shrink-0">
                    <Phone className="w-5 h-5 text-[#c16e5e]" />
                  </div>
                  <div>
                    <p className="text-stone-400 text-[10px] uppercase tracking-widest mb-1">
                      {lang === 'da' ? 'Telefon' : 'Phone'}
                    </p>
                    <a href="tel:+4530463052" className="text-lg text-stone-900 hover:text-[#c16e5e] transition-colors">
                      +45 3046 3052
                    </a>
                  </div>
                </div>
              </div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8 }}
              className="flex justify-center lg:justify-end"
            >
              <div className="w-full max-w-[380px] relative">
                <img 
                  src="https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=80,format=auto/assets/d2a473f1-f23b-48f1-982c-7e943d864f39/image_13.png" 
                  alt="Kay Elizabeth Worsfold" 
                  className="w-full h-auto object-contain rounded-2xl"
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Prices & Info Section */}
      <Section className="bg-white">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
          {/* Left Column: Prices */}
          <div className="lg:col-span-2 space-y-12">
            <div>
              <div className="flex items-center gap-4 mb-8">
                <CreditCard className="w-6 h-6 text-[#c16e5e]" />
                <h2 className="text-2xl font-serif text-stone-900">{c.prices.title}</h2>
              </div>
              <div className="bg-stone-50 p-8 rounded-2xl border border-stone-100">
                <p className="text-sm text-stone-400 mb-6 uppercase tracking-widest">{c.prices.date}</p>
                <ul className="space-y-4 mb-8">
                  {c.prices.items.map((item, i) => (
                    <li key={i} className="flex items-start gap-3 text-stone-600 leading-relaxed">
                      <div className="w-1.5 h-1.5 rounded-full bg-[#c16e5e] mt-2 shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
                <p className="text-stone-400 italic text-sm">{c.prices.note}</p>
              </div>
            </div>

            <div>
              <div className="flex items-center gap-4 mb-8">
                <Info className="w-6 h-6 text-[#c16e5e]" />
                <h2 className="text-2xl font-serif text-stone-900">{c.referral.title}</h2>
              </div>
              <div className="bg-stone-50 p-8 rounded-2xl border border-stone-100">
                <p className="text-stone-600 leading-relaxed mb-8">{c.referral.text}</p>
                {c.referral.prices && c.referral.prices.length > 0 && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                    {c.referral.prices.map((price, i) => (
                      <div key={i} className="bg-white p-4 rounded-xl shadow-sm border border-stone-100">
                        <p className="text-stone-900 font-medium">{price}</p>
                      </div>
                    ))}
                  </div>
                )}
                {c.referral.note && (
                  <p className="text-stone-400 italic text-sm">{c.referral.note}</p>
                )}
              </div>
            </div>

            {/* Getting here / Transport section */}
            <div>
              <div className="flex items-center gap-4 mb-8">
                <Map className="w-6 h-6 text-[#c16e5e]" />
                <h2 className="text-2xl font-serif text-stone-900">{c.transport.title}</h2>
              </div>
              <div className="bg-stone-50 p-8 rounded-2xl border border-stone-100 space-y-4">
                <p className="text-stone-600 leading-relaxed">{c.transport.text}</p>
                <p className="text-stone-600 leading-relaxed">{c.transport.parking}</p>
              </div>
            </div>
          </div>

          {/* Right Column: Insurance & Cancellation */}
          <div className="space-y-12">
            <div className="bg-[#c16e5e]/5 p-8 rounded-2xl border border-[#c16e5e]/10">
              <h3 className="text-xl font-serif text-stone-900 mb-4">{c.insurance.title}</h3>
              <p className="text-stone-600 text-sm leading-relaxed">{c.insurance.text}</p>
            </div>
            
            <div className="bg-[#c16e5e]/5 p-8 rounded-2xl border border-[#c16e5e]/10">
              <h3 className="text-xl font-serif text-stone-900 mb-4">{c.sygeforsikring.title}</h3>
              <p className="text-stone-600 text-sm leading-relaxed">{c.sygeforsikring.text}</p>
            </div>

            <div className="bg-taupe-500 text-white p-8 rounded-2xl">
              <div className="flex items-center gap-3 mb-4">
                <Clock className="w-5 h-5 text-taupe-100" />
                <h3 className="text-xl font-serif text-white">{c.cancellation.title}</h3>
              </div>
              <p className="text-stone-100 text-sm leading-relaxed">{c.cancellation.text}</p>
            </div>
          </div>
        </div>
      </Section>
    </div>
  );
};

export default Contact;
